/**
 * Vendor-neutral analytics. Events are pushed to `window.dataLayer`, which the
 * provider snippet added at deploy time (if any) drains.
 *
 * On prospect pages, events also go to VoxDiscover's own collector
 * (`VITE_EVENTS_URL`, served next to the site) so page behaviour reaches the
 * sender within minutes (OS §5.4, AutoOutreach PGB-02). The beacon is
 * cookieless: nothing is stored in the browser, and the collector derives a
 * salted visitor hash server-side (design doc NF-10).
 */

export type AnalyticsEvent =
  | 'promo_page_viewed'
  | 'interview_played'
  | 'interview_progress'
  | 'insight_opened'
  | 'insight_jump'
  | 'demo_cta_clicked'
  | 'page_requested'
  | 'evidence_pack_requested'

type EventProps = Record<string, string | number | boolean | null | undefined>

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

interface CollectorContext {
  slug: string
  /** AutoOutreach account id when a holdout visitor arrives on the sample page. */
  ref: string | null
  tier: string | null
  holdoutGroup: string | null
  company: string | null
  /** Operator's own visits: stored, never alerted. */
  internal: boolean
}

const INTERNAL_KEY = 'voxdiscover-internal'
const eventsUrl = import.meta.env.VITE_EVENTS_URL || (import.meta.env.PROD ? '/api/events' : '')
let context: CollectorContext | null = null

/** `?internal` once on a device marks every later visit from it as the operator's own. */
export function isInternalVisit(search: string): boolean {
  try {
    if (new URLSearchParams(search).has('internal')) localStorage.setItem(INTERNAL_KEY, '1')
    return localStorage.getItem(INTERNAL_KEY) === '1'
  } catch {
    return new URLSearchParams(search).has('internal')
  }
}

/** Starts (or, with null, stops) forwarding events to the collector. */
export function setCollectorContext(next: CollectorContext | null) {
  context = next
}

function beacon(event: AnalyticsEvent, props: EventProps) {
  if (!eventsUrl || !context) return
  const body = JSON.stringify({ event, ...context, props, path: window.location.pathname })
  try {
    // text/plain keeps sendBeacon a simple request (no CORS preflight).
    if (navigator.sendBeacon?.(eventsUrl, new Blob([body], { type: 'text/plain' }))) return
    void fetch(eventsUrl, { method: 'POST', body, keepalive: true, headers: { 'content-type': 'text/plain' } })
  } catch {
    // Analytics must never break the page.
  }
}

export function track(event: AnalyticsEvent, props: EventProps = {}) {
  const enriched = context
    ? { ...props, tier: context.tier, holdout_group: context.holdoutGroup, ref: context.ref }
    : props
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ event, ...enriched })
  beacon(event, props)
  if (import.meta.env.DEV) console.debug('[analytics]', event, enriched)
}
